import {
  Avatar,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { Icons, ItemImageArea } from 'utoniq-ui'

import {
  Maybe,
  TradeBoxFragment,
  TradeNegotiationStatus,
  User,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'

export type TradeDetailBoxProps = {
  trade: TradeBoxFragment
  disableClick?: boolean
  hiddenDivider?: boolean
  myUserId: string
}

type AvatarCellProps = {
  href?: string
  direction?: 'rtl' | 'ltr'
  user: Maybe<Pick<User, 'displayName' | 'avatar' | 'id'>>
}

export const TradeDetailBox: FC<TradeDetailBoxProps> = ({
  trade,
  disableClick,
  hiddenDivider,
  myUserId,
}) => {
  const { t } = useTranslation('myTradeDetail')
  const { routerPages, generatePath } = useRouter()

  /**
   * 自分が関連するトレードかどうか
   */
  const isMyTrade =
    myUserId === trade.request.requestedByUser?.id ||
    myUserId === trade.candidates?.[0]?.user?.id

  return (
    <>
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          bgcolor: 'background.default',
        }}
      >
        <CardContent sx={{ px: 5, py: 5, '&:last-child': { pb: 5 } }}>
          <Stack
            direction="row"
            flex={1}
            alignItems="center"
            spacing={2}
            sx={{ pb: 4 }}
          >
            <AvatarCell
              user={trade.request.requestedByUser ?? null}
              href={
                disableClick || !trade.request.requestedByUser
                  ? undefined
                  : generatePath(routerPages.UserProfile, {
                      userId: trade.request.requestedByUser?.id ?? '',
                    })
              }
            />
            <Icons.TradeArrow
              sx={{ color: 'grey.500' }}
              status={trade.status}
            />
            <AvatarCell
              user={trade.candidates?.[0]?.user ?? null}
              direction="ltr"
              href={
                disableClick || !trade.candidates?.[0]?.user
                  ? undefined
                  : generatePath(routerPages.UserProfile, {
                      userId: trade.candidates?.[0]?.user?.id ?? '',
                    })
              }
            />
          </Stack>
          <Stack direction="row" flex={1} spacing={2}>
            <ItemImageArea
              size="small"
              src={
                trade.request.providableItems[0]?.item.thumbnail.front
                  ?.mediumUri ?? ''
              }
              alignItems="flex-start"
            />
            <Stack
              flex={1}
              justifyContent="space-between"
              sx={{ maxWidth: 'calc(100% - 136px)' }}
            >
              <Stack sx={{ mr: 4 }}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      ...styleSets.lineCamp(1),
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {trade.request.providableItems[0]?.item.title}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ pt: 1 }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {
                      trade.request.providableItems[0]?.itemSerial
                        .formattedSerialNumber
                    }
                  </Typography>
                  {
                    /**
                     * @description
                     * 自分が関わるトレードの場合、トレード元・トレード先アイテムの保持枚数を表示する
                     */
                    isMyTrade && (
                      <>
                        <Divider orientation="vertical" sx={{ height: 10 }} />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icons.OwnedItems fontSize="xSmall" />
                          <Typography variant="overline" color="text.secondary">
                            {trade.request.providableItems?.[0].item?.ownedCount
                              ? t('ownedCount', {
                                  ownedCount:
                                    trade.request.providableItems?.[0].item
                                      ?.ownedCount,
                                })
                              : t('notOwned')}
                          </Typography>
                        </Stack>
                      </>
                    )
                  }
                </Stack>
              </Stack>
              <Stack sx={{ ml: 4 }}>
                <Typography
                  variant="subtitle2"
                  align="right"
                  sx={{
                    ...styleSets.lineCamp(1),
                    maxWidth: '100%',
                  }}
                >
                  {trade.candidates[0]?.candidateItems[0]?.item.title}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ pt: 1 }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {
                      trade.candidates[0]?.candidateItems[0]?.itemSerial
                        .formattedSerialNumber
                    }
                  </Typography>
                  {
                    /**
                     * @description
                     * 自分が関わるトレードの場合、トレード元・トレード先アイテムの自分の保持枚数を表示する
                     */
                    isMyTrade && (
                      <>
                        <Divider orientation="vertical" sx={{ height: 10 }} />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icons.OwnedItems fontSize="xSmall" />
                          <Typography variant="overline" color="text.secondary">
                            {trade?.candidates[0]?.candidateItems?.[0]?.item
                              ?.ownedCount
                              ? t('ownedCount', {
                                  ownedCount:
                                    trade?.candidates[0]?.candidateItems?.[0]
                                      ?.item?.ownedCount,
                                })
                              : t('notOwned')}
                          </Typography>
                        </Stack>
                      </>
                    )
                  }
                </Stack>
              </Stack>
            </Stack>
            <ItemImageArea
              size="small"
              alignItems="flex-end"
              src={
                trade.candidates[0]?.candidateItems[0]?.item.thumbnail.front
                  ?.mediumUri ?? ''
              }
            />
          </Stack>
          {trade.formattedCreatedAt && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                textAlign="right"
                color="text.secondary"
              >
                {trade.formattedCreatedAt}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
      {!hiddenDivider && (
        <>
          {trade.status === TradeNegotiationStatus.InProgress && (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              align="center"
              sx={{ pb: 4 }}
            >
              {t('waitingForApproval', {
                displayName: trade.candidates[0]?.user?.displayName ?? '',
              })}
            </Typography>
          )}
          <Divider sx={{ mx: 5 }} />
        </>
      )}
    </>
  )
}

const AvatarCell: Cell<AvatarCellProps> = ({
  user,
  href,
  direction = 'rtl',
}) => {
  return (
    <Stack
      direction={direction === 'rtl' ? 'row' : 'row-reverse'}
      flex="1 1 0"
      sx={{
        width: 'calc((100% - 40px) / 2)',
      }}
    >
      <Stack
        href={href}
        component={href ? Link : Stack}
        direction={direction === 'rtl' ? 'row' : 'row-reverse'}
        alignItems="center"
        spacing={2}
        sx={{
          textDecoration: 'none',
          width: 'fit-content',
        }}
      >
        <Avatar
          src={user?.avatar?.smallUri ?? ''}
          alt={user?.displayName ?? ''}
          sx={{ width: 28, height: 28 }}
        />
        <Typography
          color="text.secondary"
          variant="overline"
          sx={{
            ...styleSets.lineCamp(1),
            maxWidth: '100%',
            textTransform: 'none',
          }}
        >
          {user?.displayName}
        </Typography>
      </Stack>
    </Stack>
  )
}
